import React, { useState } from 'react';
import { useSelector } from 'react-redux';
import classNames from 'classnames';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import {
	Typography,
	TableRow,
	TableCell,
	IconButton,
	Collapse,
	Icon,
	Box,
	Grid,
	Tooltip,
} from '@mui/material';
import { RiPencilLine, RiArrowDownSLine } from 'react-icons/ri';
import VisibilityBadge from 'components/VisibilityBadge';
import errorHandler from 'common/services/errorHandler';

import { DeliveryOptionWithProducts } from 'common/types';
import { formatPrice } from 'common/utils/common';
import { useTranslation } from 'services/localization/useTranslation';
import { push, Routes } from 'routing';
import useShopFormat from 'hooks/useShopFormat';
import { hideDeliveryOption } from 'common/api/frontend';

import * as StockSelectors from 'selectors/StockSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { getDayTypeTranslation } from 'common/utils/translations';
import { deliveryTypeHasPickupPrice } from '../DeliveryForm/utils';
import { getBufferTimeAsString } from 'common/modules/atoms/bufferTimes';

interface Props {
	row: DeliveryOptionWithProducts;
}

const DeliveryOptionRow = ({ row }: Props) => {
	const [open, setOpen] = useState(false);

	const classes = useStyles();
	const { t } = useTranslation();
	const { localFormat } = useShopFormat();

	const activeLocationProducts = useSelector(StockSelectors.activeLocationProducts);
	const shopDateFormat = useSelector(ShopSelectors.getShopDateFormat);

	const productListString = row.products.map((p) => p.name.def).join(', ');

	const handleToggleVisibility = async (id: string, isHidden: boolean) => {
		try {
			await hideDeliveryOption(id, isHidden);
		} catch (e) {
			errorHandler.report(e);
		}
	};

	const renderCollapseButtonCell = () => (
		<TableCell className={classes.fitContent}>
			<IconButton size="small" onClick={() => setOpen(!open)}>
				<Icon className={classNames(classes.collapseIcon, open && classes.collapseIconOpen)}>
					<RiArrowDownSLine />
				</Icon>
			</IconButton>
		</TableCell>
	);

	const renderNameCell = () => (
		<TableCell className={classes.nameCell}>
			{row.name.def}
			<Collapse in={open}>
				<Box mt={1} />
				<Typography variant="caption" color="secondary">
					{row.description.def}
				</Typography>
			</Collapse>
		</TableCell>
	);

	const renderProductsCell = () => {
		const partialProducts = (
			<>
				<Collapse in={!open}>
					<Box className={classes.productsOverview}>
						<Box className={classes.productsName}>{row.products[0]?.name.def ?? ''}</Box>
						{row.products.length > 1 && (
							<Typography variant="caption" className={classes.productsBadge}>
								+{row.products.length - 1}
							</Typography>
						)}
					</Box>
				</Collapse>
				<Collapse in={open}>
					<Box pt={0.25}>{productListString}</Box>
				</Collapse>
			</>
		);
		return (
			<TableCell className={classes.productsCell}>
				{row.products.length === activeLocationProducts.length
					? t('common:products.allProducts', 'All products')
					: row.products.length === 0
					? t('delivery.noProductsSelected', 'No products selected')
					: partialProducts}
			</TableCell>
		);
	};

	const renderPriceString = (price: number) =>
		price === 0 ? t('pricing.free') : formatPrice(price);

	const renderPriceCell = () => (
		<TableCell className={classes.priceCell} align="right">
			{
				<>
					<Collapse in={!open}>{renderPriceString(row.price.delivery + row.price.pickup)}</Collapse>
					<Collapse in={open}>
						<Grid container>
							<Grid item container spacing={1} wrap="nowrap" justifyContent="space-between">
								<Grid item>
									<Typography variant="caption" color="secondary">
										{t('delivery.delivery', 'Delivery')}
									</Typography>
								</Grid>
								<Grid item>{renderPriceString(row.price.delivery)}</Grid>
							</Grid>
							{deliveryTypeHasPickupPrice(row.type) && (
								<Grid item container spacing={1} wrap="nowrap" justifyContent="space-between">
									<Grid item>
										<Typography variant="caption" color="secondary">
											{t('delivery.pickup', 'Pickup')}
										</Typography>
									</Grid>
									<Grid item>{renderPriceString(row.price.pickup)}</Grid>
								</Grid>
							)}
						</Grid>
					</Collapse>
				</>
			}
		</TableCell>
	);

	const renderOrderLatestCell = () => (
		<TableCell>
			{getBufferTimeAsString({
				bufferTime: row.handlingTimes?.preparationTime,
				direction: 'before',
				shopDateFormat,
				t,
			}) || t('delivery.nA', 'n/a')}
		</TableCell>
	);

	const renderAvailableCell = () => (
		<TableCell className={classes.fitContent}>
			{row.deliveryPeriods?.map((period, pIndex) => (
				<Box key={pIndex}>
					<Box whiteSpace="break-spaces">
						<span className={classes.fitContent}>
							{!period.endDate && `${t('common:times.from')} `}
							{localFormat(period.startDate, 'MMM DD, YYYY')}
							{period.endDate && ' - '}
						</span>
						{period.endDate && (
							<span className={classes.fitContent}>
								{localFormat(period.endDate, 'MMM DD, YYYY')}
							</span>
						)}
					</Box>
					<Collapse in={open}>
						<Box mb={2}>
							{period.times.map((time, tIndex) => (
								<Box mt={1} key={tIndex}>
									<Typography
										variant="caption"
										color="secondary"
										className={classes.availableDayType}
									>
										{getDayTypeTranslation(time.dayType, t)}
									</Typography>
									{time.slots.map((slot, sIndex) => (
										<Box key={sIndex}>
											{slot.startTime} - {slot.endTime}
										</Box>
									))}
								</Box>
							))}
						</Box>
					</Collapse>
				</Box>
			))}
		</TableCell>
	);

	const renderActionsCell = () => (
		<TableCell className={classes.fitContent}>
			<IconButton
				size="small"
				className={classes.icon}
				onClick={() => push(Routes.SingleDelivery(row.id))}
			>
				<RiPencilLine />
			</IconButton>
			<Tooltip
				title={t('delivery.editDeliveryOptionVisibility', 'Toggle delivery option visibility')}
			>
				<IconButton
					size="small"
					className={classes.icon}
					onClick={() => handleToggleVisibility(row.id, !row?.hidden)}
				>
					<VisibilityBadge isHidden={row?.hidden ?? false} onlyIcon={true} />
				</IconButton>
			</Tooltip>
		</TableCell>
	);

	return (
		<>
			<TableRow className={classes.tableRow}>
				{renderCollapseButtonCell()}
				{renderNameCell()}
				{renderProductsCell()}
				{renderPriceCell()}
				{renderOrderLatestCell()}
				{renderAvailableCell()}
				{renderActionsCell()}
			</TableRow>
		</>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		tableRow: {
			'& > *': {
				borderBottom: '1px solid #d8d8d8',
				verticalAlign: 'baseline',
			},
		},
		collapseIcon: {
			display: 'flex',
			alignItems: 'center',
			justifyContent: 'center',
			transition: '0.50s',
		},
		collapseIconOpen: {
			transform: 'rotate(-180deg)',
			transition: '0.50s',
		},
		nameCell: {
			paddingLeft: 0,
			width: 170,
		},
		fitContent: {
			whiteSpace: 'nowrap',
			width: '1%',
		},
		icon: {
			'&:last-child': {
				marginLeft: theme.spacing(1),
			},
		},
		productsCell: {
			width: 250,
		},
		productsOverview: {
			display: 'flex',
			alignItems: 'baseline',
			justifyContent: 'flex-start',
		},
		productsName: {
			whiteSpace: 'nowrap',
			overflow: 'hidden',
			textOverflow: 'ellipsis',
			maxWidth: 160,
		},
		productsBadge: {
			border: `1px solid ${theme.palette.border.inputOutline}`,
			borderRadius: 16,
			padding: theme.spacing(0.25, 1.5),
			marginLeft: theme.spacing(2),
		},
		priceCell: {
			width: 150,
		},
		availableDayType: {
			textTransform: 'uppercase',
		},
		visibilityToggle: {
			display: 'inline-block',
			verticalAlign: 'middle',
			marginLeft: theme.spacing(1),
		},
	}),
);

export default DeliveryOptionRow;
